import * as React from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, Typography, makeStyles, Button, Collapse, Grid } from '@material-ui/core';
import { useIifImportSelector, IifImportSelectors } from '../store/modules/iifImport';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';
import { DateTime } from 'luxon'
import { classNames } from '../shared';
import AudCurrencyFormatter from '../utilities/currencyFormatter';
import { TaxMode, ApplicationSettingsSelectors, useApplicationSettingsSelector } from '../store/modules/applicationSettings';
import { red } from '@material-ui/core/colors';

interface ISalesItemDistributionViewProps {
    distributionIndex: number;
    distributionPosition: number;
}

const useStyles = makeStyles(theme => ({
    error: {
        background: `${red[100]} !important`
    },
    button: {
        textAlign: 'center',
        minWidth: 'auto',
        padding: theme.spacing(1),

        '& svg': {
            transition: 'all 0.25s linear'
        }
    },
    expanded: {
        '& svg': {
            transform: 'rotate(180deg)'
        }
    },
    noPadding: {
        padding: theme.spacing(0)
    },
    padding: {
        padding: theme.spacing(2)
    }
}))

const SalesItemDistributionView: React.FunctionComponent<ISalesItemDistributionViewProps> = ({
    distributionIndex,
    distributionPosition
}) => {
    const distribution = useIifImportSelector(state => IifImportSelectors.selectSalesItemDistribution(state, distributionIndex));

    const taxMode = useApplicationSettingsSelector(ApplicationSettingsSelectors.selectTaxMode);

    const classes = useStyles();
    const [expanded, setExpanded] = React.useState(false);
    const [show, setShow] = React.useState(false);

    const onExpandClick = React.useCallback(() => {
        if (expanded) {
            setShow(!show);
        }
        else {
            setExpanded(true);
            setShow(true);
        }
    }, [setShow, show, expanded, setExpanded]);

    const onExited = React.useCallback(() => {
        setExpanded(false);
    }, [setExpanded])

    if (!distribution) {
        return null;
    }

    return (
        <>
            <TableRow>
                <TableCell>
                    <Button
                        className={classNames(classes.button, { [classes.expanded]: show })}
                        color="inherit"
                        onClick={onExpandClick}
                    >
                        <FontAwesomeIcon icon={faChevronDown} fixedWidth />
                    </Button>
                </TableCell>
                <TableCell>{distributionPosition}</TableCell>
                <TableCell className={classNames({ [classes.error]: distribution.accountError })}>{distribution.itemAccount?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: distribution.classError })}>{distribution.class?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: distribution.itemError })}>{distribution.item?.value}</TableCell>
                <TableCell>{distribution.description}</TableCell>
                <TableCell className={classNames({ [classes.error]: distribution.taxCodeError })}>{distribution.taxCodeRef?.value}</TableCell>
                <TableCell align="right">
                    {
                        taxMode === TaxMode.Excluded ?
                            AudCurrencyFormatter.format(distribution.amount ?? 0) :
                            AudCurrencyFormatter.format(distribution.taxInclusiveAmount ?? 0)
                    }
                </TableCell>
            </TableRow>
            {
                expanded && (
                    <TableRow>
                        <TableCell className={classes.noPadding} />
                        <TableCell colSpan={5} className={classes.noPadding}>
                            <Collapse in={show} appear={true} onExited={onExited} >
                                <div className={classes.padding}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={3} lg={2}>
                                            <Typography>
                                                <strong>
                                                    Amount
                                                </strong>
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography>
                                                {AudCurrencyFormatter.format(distribution.amount ?? 0)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={3} lg={2}>
                                            <Typography>
                                                <strong>
                                                    Tax
                                                </strong>
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography>
                                                {AudCurrencyFormatter.format(distribution.taxAmount ?? 0)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </div>
                            </Collapse>
                        </TableCell>
                    </TableRow>
                )
            }
        </>
    );
}

export default SalesItemDistributionView;


// WEBPACK FOOTER //
// ./src/components/SalesItemDistributionView.tsx